// src/pages/ChatPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar, Badge, Box, Chip, CircularProgress, Divider, IconButton,
  InputBase, List, ListItem, ListItemAvatar, ListItemText, Paper,
  Tooltip, Typography,
} from '@mui/material';
import {
  ArrowBack, EmojiEmotions, Info as InfoIcon, Mic, Poll as PollIcon,
  Reply as ReplyIcon, Send, Close as CloseIcon,
} from '@mui/icons-material';
import {
  addDoc, collection, doc, getDoc, getDocs, onSnapshot, orderBy,
  query, serverTimestamp, setDoc, updateDoc, runTransaction,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import EmojiPicker from 'emoji-picker-react';

// ── Constantes ────────────────────────────────────────────────────────────
const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
const PURPLE = '#7C3AED';

const ROLE_LABELS = { admin: 'Admin', teacher: 'Prof', parent: 'Parent', student: 'Élève' };
const ROLE_COLORS = { admin: '#E53935', teacher: '#1E88E5', parent: '#43A047', student: '#FB8C00' };

const normalizeRole = (r = '') => {
  const v = r.toLowerCase();
  if (v.includes('admin')) return 'admin';
  if (v.includes('prof') || v.includes('teacher')) return 'teacher';
  if (v.includes('parent')) return 'parent';
  return 'student';
};

const fmtTime = (ts) => {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (ts) => {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  const da = a?.toDate ? a.toDate() : new Date(a.seconds * 1000);
  const db2 = b?.toDate ? b.toDate() : new Date(b.seconds * 1000);
  return da.toDateString() === db2.toDateString();
};

// ── RoleChip ─────────────────────────────────────────────────────────────
const RoleChip = ({ role }) => {
  const key = normalizeRole(role);
  return (
    <Box component="span" sx={{
      fontSize: 10, fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 1,
      bgcolor: (ROLE_COLORS[key] || '#999') + '20',
      color: ROLE_COLORS[key] || '#999', ml: 0.5,
    }}>
      {ROLE_LABELS[key] || role}
    </Box>
  );
};

// ── ReplyPreview (dans la bulle) ──────────────────────────────────────────
const ReplyPreview = ({ replyTo, isMe }) => (
  <Box sx={{
    borderLeft: `3px solid ${isMe ? 'rgba(255,255,255,0.6)' : PURPLE}`,
    pl: 1, mb: 0.5, opacity: 0.85,
    bgcolor: isMe ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)',
    borderRadius: '0 4px 4px 0', py: 0.3,
  }}>
    <Typography variant="caption" sx={{ fontWeight: 700, color: isMe ? '#fff' : PURPLE, display: 'block' }}>
      {replyTo.senderName}
    </Typography>
    <Typography variant="caption" sx={{ color: isMe ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}
      noWrap>
      {replyTo.content}
    </Typography>
  </Box>
);

// ── PollBubble ────────────────────────────────────────────────────────────
const PollBubble = ({ poll, messageId, chatId, myUid, isMe }) => {
  const options = poll.options || [];
  const total = options.reduce((s, o) => s + (o.votes?.length || 0), 0);
  const hasVotedAny = options.some(o => (o.votes || []).includes(myUid));
  const isClosed = !!poll.closedAt;

  const vote = async (optId) => {
    if (isClosed) return;
    const msgRef = doc(db, 'chats', chatId, 'messages', messageId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(msgRef);
      if (!snap.exists()) return;
      const p = { ...snap.data().poll };
      const opts = p.options.map(o => {
        const votes = [...(o.votes || [])];
        if (o.id === optId) {
          const idx = votes.indexOf(myUid);
          idx >= 0 ? votes.splice(idx, 1) : votes.push(myUid);
        } else if (!p.multipleChoice) {
          const idx = votes.indexOf(myUid);
          if (idx >= 0) votes.splice(idx, 1);
        }
        return { ...o, votes };
      });
      tx.update(msgRef, { poll: { ...p, options: opts } });
    });
  };

  const accent = isMe ? '#fff' : PURPLE;

  return (
    <Box sx={{ minWidth: 220, maxWidth: 280 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <PollIcon sx={{ fontSize: 14, color: accent, opacity: 0.7 }} />
        <Typography variant="caption" sx={{ color: accent, opacity: 0.7 }}>
          {isClosed ? 'Sondage terminé' : 'Sondage'}{poll.multipleChoice ? ' • Choix multiple' : ''}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700, color: isMe ? '#fff' : 'text.primary', mb: 1 }}>
        {poll.question}
      </Typography>
      {options.map(opt => {
        const voted = (opt.votes || []).includes(myUid);
        const count = (opt.votes || []).length;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <Box key={opt.id} onClick={() => vote(opt.id)}
            sx={{ mb: 0.8, cursor: isClosed ? 'default' : 'pointer', position: 'relative',
              borderRadius: 1.5, overflow: 'hidden',
              bgcolor: isMe ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            }}>
            {(total > 0) && (
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${pct}%`, bgcolor: isMe ? 'rgba(255,255,255,0.2)' : PURPLE + '25',
                transition: 'width 0.4s', borderRadius: 1.5 }} />
            )}
            <Box sx={{ position: 'relative', px: 1.5, py: 0.8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: voted ? 700 : 400, color: isMe ? '#fff' : 'text.primary' }}>
                {opt.text}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {(total > 0) && (
                  <Typography variant="caption" sx={{ color: accent, opacity: 0.8 }}>{pct}%</Typography>
                )}
                {voted && <Box component="span" sx={{ fontSize: 10, color: accent }}>✓</Box>}
              </Box>
            </Box>
          </Box>
        );
      })}
      <Typography variant="caption" sx={{ color: accent, opacity: 0.6 }}>
        {total} vote{total > 1 ? 's' : ''}
      </Typography>
    </Box>
  );
};

// ── ReactionsRow ──────────────────────────────────────────────────────────
const ReactionsRow = ({ reactions, myUid, messageId, chatId }) => {
  const items = Object.entries(reactions).filter(([, v]) => v.length > 0);
  if (!items.length) return null;

  const toggle = async (emoji) => {
    const msgRef = doc(db, 'chats', chatId, 'messages', messageId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(msgRef);
      if (!snap.exists()) return;
      const r = { ...(snap.data().reactions || {}) };
      const list = [...(r[emoji] || [])];
      const idx = list.indexOf(myUid);
      idx >= 0 ? list.splice(idx, 1) : list.push(myUid);
      r[emoji] = list;
      tx.update(msgRef, { reactions: r });
    });
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
      {items.map(([emoji, voters]) => (
        <Chip key={emoji} label={`${emoji} ${voters.length}`} size="small"
          onClick={() => toggle(emoji)}
          sx={{ height: 22, fontSize: 12, cursor: 'pointer',
            bgcolor: voters.includes(myUid) ? PURPLE + '20' : '#f0f0f0',
            border: voters.includes(myUid) ? `1px solid ${PURPLE}50` : '1px solid transparent',
          }} />
      ))}
    </Box>
  );
};

// ── MessageBubble ─────────────────────────────────────────────────────────
const MessageBubble = ({ msg, prev, myUid, usersById, chatId, onReply, onReaction }) => {
  const isMe = msg.senderId === myUid;
  const sender = usersById[msg.senderId] || {};
  const senderName = msg.senderName || sender.displayName || sender.email?.split('@')[0] || '?';
  const senderRole = msg.senderRole || sender.role || '';
  const reactions = msg.reactions || {};
  const replyTo = msg.replyTo || null;

  const showDate = !prev || !isSameDay(prev.createdAt, msg.createdAt);
  const showSender = !isMe && (!prev || prev.senderId !== msg.senderId);

  return (
    <>
      {/* Séparateur de date */}
      {showDate && (
        <Box sx={{ textAlign: 'center', my: 2 }}>
          <Chip label={fmtDate(msg.createdAt)} size="small"
            sx={{ fontSize: 11, color: 'text.secondary', bgcolor: '#f5f5f5' }} />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', mb: 0.5, px: 1 }}>
        {/* Avatar */}
        {!isMe && (
          <Avatar sx={{ width: 32, height: 32, mr: 1, alignSelf: 'flex-end', mb: 0.5,
            bgcolor: (ROLE_COLORS[normalizeRole(senderRole)] || '#999') + '25',
            color: ROLE_COLORS[normalizeRole(senderRole)] || '#999', fontSize: 13 }}>
            {senderName[0]?.toUpperCase()}
          </Avatar>
        )}

        <Box sx={{ maxWidth: '72%' }}>
          {/* Nom + rôle */}
          {showSender && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3, ml: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
                {senderName}
              </Typography>
              {senderRole && <RoleChip role={senderRole} />}
            </Box>
          )}

          {/* Bulle */}
          <Box
            onDoubleClick={() => onReaction(msg.id)}
            sx={{ position: 'relative',
              '&:hover .msg-actions': { opacity: 1 },
            }}>
            <Box sx={{
              p: 1.5, borderRadius: 2.5,
              borderBottomRightRadius: isMe ? 4 : 20,
              borderBottomLeftRadius: isMe ? 20 : 4,
              bgcolor: isMe ? PURPLE : '#fff',
              color: isMe ? '#fff' : 'text.primary',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}>
              {replyTo && <ReplyPreview replyTo={replyTo} isMe={isMe} />}

              {/* Contenu */}
              {msg.type === 'voice' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
                  <Box component="span" sx={{ fontSize: 20 }}>🎤</Box>
                  <Typography variant="caption" sx={{ color: isMe ? '#fff' : 'text.secondary' }}>
                    Message vocal {msg.durationSeconds ? `• ${msg.durationSeconds}s` : ''}
                  </Typography>
                  <IconButton size="small" href={msg.audioUrl} target="_blank"
                    sx={{ color: isMe ? '#fff' : PURPLE, ml: 'auto' }}>
                    ▶
                  </IconButton>
                </Box>
              ) : msg.type === 'poll' ? (
                <PollBubble poll={msg.poll} messageId={msg.id}
                  chatId={chatId} myUid={myUid} isMe={isMe} />
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: isMe ? '#fff' : 'text.primary' }}>
                  {msg.text}
                </Typography>
              )}

              {/* Heure + lu */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.65, color: isMe ? '#fff' : 'text.secondary' }}>
                  {fmtTime(msg.createdAt)}
                </Typography>
                {isMe && (
                  <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.8, color: (msg.readBy?.length > 1) ? '#90CAF9' : (isMe ? '#fff' : 'text.secondary') }}>
                    {msg.readBy?.length > 1 ? '✓✓' : '✓'}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Actions au hover */}
            <Box className="msg-actions" sx={{
              opacity: 0, transition: 'opacity 0.15s',
              position: 'absolute', top: -16,
              [isMe ? 'left' : 'right']: 0,
              display: 'flex', gap: 0.3,
              bgcolor: '#fff', borderRadius: 2, boxShadow: 1, px: 0.5, py: 0.2,
            }}>
              {REACTIONS.slice(0, 4).map(e => (
                <Box key={e} onClick={() => onReaction(msg.id, e)}
                  sx={{ cursor: 'pointer', fontSize: 16, lineHeight: 1.5,
                    '&:hover': { transform: 'scale(1.3)' }, transition: 'transform 0.1s' }}>
                  {e}
                </Box>
              ))}
              <Tooltip title="Répondre">
                <IconButton size="small" onClick={() => onReply(msg)}>
                  <ReplyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Réactions */}
          <Box sx={{ ml: 0.5 }}>
            <ReactionsRow reactions={reactions} myUid={myUid} messageId={msg.id} chatId={chatId} />
          </Box>
        </Box>
      </Box>
    </>
  );
};

// ── CreatePollDialog ──────────────────────────────────────────────────────
const CreatePollDialog = ({ open, onClose, onSubmit }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multipleChoice, setMultipleChoice] = useState(false);

  const valid = question.trim() && options.filter(o => o.trim()).length >= 2;

  const submit = () => {
    if (!valid) return;
    const poll = {
      question: question.trim(),
      options: options.filter(o => o.trim()).map((text, i) => ({
        id: `opt_${i}_${Date.now()}`, text: text.trim(), votes: [],
      })),
      multipleChoice,
      closedAt: null,
    };
    onSubmit(poll);
    setQuestion(''); setOptions(['', '']); setMultipleChoice(false);
  };

  if (!open) return null;

  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1300,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}>
      <Box sx={{ bgcolor: '#fff', borderRadius: '20px 20px 0 0', p: 3, width: '100%', maxWidth: 500 }}
        onClick={e => e.stopPropagation()}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">Créer un sondage</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>

        <InputBase fullWidth placeholder="Question…" value={question}
          onChange={e => setQuestion(e.target.value)}
          sx={{ bgcolor: '#f5f5f5', borderRadius: 2, px: 2, py: 1, mb: 2, display: 'block' }} />

        {options.map((opt, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <InputBase fullWidth placeholder={`Option ${i + 1}`} value={opt}
              onChange={e => { const n = [...options]; n[i] = e.target.value; setOptions(n); }}
              sx={{ bgcolor: '#f5f5f5', borderRadius: 2, px: 2, py: 0.8, flex: 1 }} />
            {options.length > 2 && (
              <IconButton size="small" onClick={() => setOptions(options.filter((_, j) => j !== i))}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}

        {options.length < 6 && (
          <Box onClick={() => setOptions([...options, ''])}
            sx={{ color: PURPLE, cursor: 'pointer', fontSize: 13, mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            + Ajouter une option
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box component="input" type="checkbox" checked={multipleChoice}
            onChange={e => setMultipleChoice(e.target.checked)} />
          <Typography variant="body2">Choix multiple</Typography>
        </Box>

        <Box onClick={submit}
          sx={{ bgcolor: valid ? PURPLE : '#ccc', color: '#fff', borderRadius: 2,
            py: 1.5, textAlign: 'center', cursor: valid ? 'pointer' : 'default',
            fontWeight: 700, fontSize: 14 }}>
          Envoyer le sondage
        </Box>
      </Box>
    </Box>
  );
};

// ── ReplyBanner ───────────────────────────────────────────────────────────
const ReplyBanner = ({ replyTo, onCancel }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.8,
    bgcolor: PURPLE + '10', borderTop: `1px solid ${PURPLE}30` }}>
    <ReplyIcon sx={{ color: PURPLE, mr: 1, fontSize: 18 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: PURPLE, fontWeight: 700, display: 'block' }}>
        Répondre à {replyTo.senderName}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
        {replyTo.content}
      </Typography>
    </Box>
    <IconButton size="small" onClick={onCancel}><CloseIcon fontSize="small" /></IconButton>
  </Box>
);

// ══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════
export default function ChatPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);
  const me = auth.currentUser;

  const [loading, setLoading] = useState(true);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState({});
  const [usersById, setUsersById] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showPollDialog, setShowPollDialog] = useState(false);
  const [reactionTarget, setReactionTarget] = useState(null); // messageId pour picker complet

  const bottomRef = useRef(null);
  const typingTimer = useRef(null);
  const inputRef = useRef(null);

  // ── Chargement ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!me || !chatId) { setLoading(false); return; }
    let unsubMsgs = () => {}, unsubMembers = () => {};

    const init = async () => {
      setLoading(true);
      try {
        // Charger le chat
        const chatDoc = await getDoc(doc(db, 'chats', chatId));
        if (!chatDoc.exists()) { setLoading(false); return; }
        setCurrentChat({ id: chatDoc.id, ...chatDoc.data() });

        // Charger les users des participants
        const participants = chatDoc.data().participants || [];
        const byId = {};
        await Promise.all(participants.map(async uid => {
          try {
            const u = await getDoc(doc(db, 'users', uid));
            if (u.exists()) byId[uid] = { id: u.id, ...u.data() };
          } catch {}
        }));
        setUsersById(byId);

        // Messages temps réel — champ createdAt (nouveau) avec fallback timestamp
        const qMsgs = query(
          collection(db, 'chats', chatId, 'messages'),
          orderBy('createdAt', 'desc'),
        );
        unsubMsgs = onSnapshot(qMsgs, snap => {
          setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse());
          // Marquer comme lu
          setDoc(doc(db, 'chats', chatId, 'members', me.uid),
            { lastReadAt: serverTimestamp() }, { merge: true });
        });

        // Membres (typing)
        unsubMembers = onSnapshot(collection(db, 'chats', chatId, 'members'), snap => {
          const m = {}; snap.forEach(d => (m[d.id] = d.data()));
          setMembers(m);
        });

      } finally { setLoading(false); }
    };

    init();
    return () => { unsubMsgs(); unsubMembers(); };
  }, [chatId, me]);

  // Scroll auto
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Envoi ───────────────────────────────────────────────────────────────
  const sendMessage = useCallback(async ({ type = 'text', content = {} } = {}) => {
    if (!me || !currentChat) return;

    const userData = usersById[me.uid] || {};
    const senderName = userData.displayName || me.email?.split('@')[0] || 'Utilisateur';
    const senderRole = userData.role || '';

    const base = {
      type, senderId: me.uid, senderName, senderRole,
      readBy: [me.uid], reactions: {},
      createdAt: serverTimestamp(),
      ...content,
    };
    if (replyTo) { base.replyTo = replyTo; setReplyTo(null); }

    let preview = '';
    if (type === 'text') preview = content.text || '';
    else if (type === 'voice') preview = '🎤 Message vocal';
    else if (type === 'poll') preview = `📊 ${content.poll?.question || 'Sondage'}`;

    await addDoc(collection(db, 'chats', chatId, 'messages'), base);
    await updateDoc(doc(db, 'chats', chatId), {
      lastMessageAt: serverTimestamp(),
      lastMessageText: preview,
      lastMessageSender: me.uid,
      lastMessageSenderName: senderName,
    });

    setNewMessage('');
    setShowEmoji(false);
  }, [me, currentChat, chatId, replyTo, usersById]);

  const sendText = useCallback(() => {
    const text = newMessage.trim();
    if (!text) return;
    sendMessage({ type: 'text', content: { text } });
  }, [newMessage, sendMessage]);

  const sendPoll = useCallback((poll) => {
    sendMessage({ type: 'poll', content: { poll } });
    setShowPollDialog(false);
  }, [sendMessage]);

  // ── Typing ──────────────────────────────────────────────────────────────
  const handleTyping = (val) => {
    setNewMessage(val);
    if (!currentChat || !me) return;
    clearTimeout(typingTimer.current);
    setDoc(doc(db, 'chats', chatId, 'members', me.uid),
      { isTyping: true, lastTypingAt: serverTimestamp() }, { merge: true });
    typingTimer.current = setTimeout(() => {
      setDoc(doc(db, 'chats', chatId, 'members', me.uid),
        { isTyping: false }, { merge: true });
    }, 1500);
  };

  // ── Réaction ─────────────────────────────────────────────────────────────
  const handleReaction = useCallback(async (messageId, emoji) => {
    if (!emoji) { setReactionTarget(messageId); return; }
    const msgRef = doc(db, 'chats', chatId, 'messages', messageId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(msgRef);
      if (!snap.exists()) return;
      const r = { ...(snap.data().reactions || {}) };
      const list = [...(r[emoji] || [])];
      const idx = list.indexOf(me.uid);
      idx >= 0 ? list.splice(idx, 1) : list.push(me.uid);
      r[emoji] = list;
      tx.update(msgRef, { reactions: r });
    });
    setReactionTarget(null);
  }, [chatId, me]);

  // ── Typers ───────────────────────────────────────────────────────────────
  const typers = Object.entries(members)
    .filter(([uid, v]) => uid !== me?.uid && v?.isTyping)
    .map(([uid]) => usersById[uid]?.displayName || 'Quelqu\'un');

  // ── Reply ────────────────────────────────────────────────────────────────
  const handleReply = useCallback((msg) => {
    let content = '';
    if (msg.type === 'voice') content = '🎤 Message vocal';
    else if (msg.type === 'poll') content = `📊 ${msg.poll?.question || 'Sondage'}`;
    else content = msg.text || '';
    setReplyTo({ messageId: msg.id, content, senderName: msg.senderName || '?' });
    inputRef.current?.focus();
  }, []);

  // ── Guards ───────────────────────────────────────────────────────────────
  if (!me) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography>Veuillez vous connecter.</Typography>
    </Box>
  );

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
      <CircularProgress sx={{ color: PURPLE }} />
    </Box>
  );

  if (!currentChat) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography>Chat introuvable.</Typography>
    </Box>
  );

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F5F5' }}>

      {/* Header */}
      <Paper elevation={1} sx={{ px: 2, py: 1.5, borderRadius: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" noWrap>
            {currentChat.name || 'Chat'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {currentChat.participants?.length || 0} participants
            {typers.length > 0 && (
              <Box component="span" sx={{ color: PURPLE, fontStyle: 'italic', ml: 1 }}>
                • {typers.join(', ')} {typers.length > 1 ? 'écrivent…' : 'écrit…'}
              </Box>
            )}
          </Typography>
        </Box>
        <IconButton onClick={() => navigate(`/chats/${chatId}/info`)}>
          <InfoIcon />
        </IconButton>
      </Paper>

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
            <Typography variant="body2">Aucun message pour l'instant.<br />Soyez le premier à écrire !</Typography>
          </Box>
        )}
        {messages.map((msg, i) => (
          <MessageBubble key={msg.id} msg={msg} prev={messages[i - 1] || null}
            myUid={me.uid} usersById={usersById} chatId={chatId}
            onReply={handleReply} onReaction={handleReaction} />
        ))}
        <div ref={bottomRef} />
      </Box>

      {/* Picker réaction complet (optionnel) */}
      {reactionTarget && (
        <Box sx={{ position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          bgcolor: '#fff', borderRadius: 3, boxShadow: 4, p: 1.5, display: 'flex', gap: 1.5, zIndex: 100 }}>
          {REACTIONS.map(e => (
            <Box key={e} onClick={() => handleReaction(reactionTarget, e)}
              sx={{ fontSize: 24, cursor: 'pointer', '&:hover': { transform: 'scale(1.3)' }, transition: 'transform 0.1s' }}>
              {e}
            </Box>
          ))}
          <IconButton size="small" onClick={() => setReactionTarget(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Emoji picker */}
      {showEmoji && (
        <Box sx={{ borderTop: '1px solid #eee' }}>
          <EmojiPicker onEmojiClick={e => setNewMessage(prev => prev + e.emoji)} height={300} />
        </Box>
      )}

      {/* Reply banner */}
      {replyTo && <ReplyBanner replyTo={replyTo} onCancel={() => setReplyTo(null)} />}

      {/* Saisie */}
      <Paper elevation={2} sx={{ px: 1.5, py: 1, borderRadius: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Sondage">
          <IconButton onClick={() => setShowPollDialog(true)}>
            <PollIcon sx={{ color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Emoji">
          <IconButton onClick={() => setShowEmoji(v => !v)}>
            <EmojiEmotions sx={{ color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>

        <InputBase fullWidth multiline maxRows={4} inputRef={inputRef}
          placeholder="Message…" value={newMessage} onChange={e => handleTyping(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendText())}
          sx={{ bgcolor: '#f5f5f5', borderRadius: 3, px: 2, py: 0.8, fontSize: 14 }} />

        <IconButton onClick={sendText} disabled={!newMessage.trim()}
          sx={{ color: newMessage.trim() ? PURPLE : 'text.disabled' }}>
          <Send />
        </IconButton>
      </Paper>

      {/* Dialog sondage */}
      <CreatePollDialog open={showPollDialog}
        onClose={() => setShowPollDialog(false)} onSubmit={sendPoll} />
    </Box>
  );
}